import React from 'react';
import { Bell, ChevronUp } from 'lucide-react';

interface PushNotificationPreviewProps {
  title: string;
  body: string;
  deepLink?: string | null;
}

/**
 * Live preview of how an admin broadcast will render as a native push
 * notification on a creator's lock screen. The outer phone frame / status
 * bar / clock mirrors UpdateModalPreview's style; the notification banner
 * itself is styled after an actual Android lock-screen notification (ring
 * icon, "App • now" + bell/chevron controls) rather than a generic card.
 * Fills the height its flex parent gives it (flex-1) instead of a fixed
 * pixel height, so it reads as a real phone screen — dark background all
 * the way down — rather than a short graphic floating in a taller box.
 */
export default function PushNotificationPreview({
  title,
  body,
  deepLink,
}: PushNotificationPreviewProps) {
  return (
    <div
      className="w-full max-w-[375px] mx-auto flex-1 min-h-0 rounded-[2rem] shadow-xl border-[8px] border-gray-800 overflow-hidden flex flex-col"
      style={{ background: 'linear-gradient(180deg, #241a35 0%, #0A0A0A 55%)' }}
    >
      {/* Status bar */}
      <div className="flex items-center justify-between px-6 py-2 text-xs font-semibold text-white/90">
        <span>1:00</span>
        <span>●●●</span>
      </div>

      {/* Lock screen clock */}
      <div className="flex flex-col items-center pt-3 pb-6">
        <span className="text-white/70 text-sm font-medium">Today</span>
        <span className="text-white text-6xl font-semibold tracking-tight">9:41</span>
      </div>

      {/* Notification banner */}
      <div className="px-3 pb-5">
        <div
          className="rounded-2xl p-3"
          style={{ background: '#303134' }}
        >
          <div className="flex items-center gap-1.5">
            <div className="h-5 w-5 rounded-full ring-2 ring-[#E91E8C] overflow-hidden bg-white flex items-center justify-center shrink-0">
              <img src="/bmc-logo.png" alt="BMC" className="h-full w-full object-cover" />
            </div>
            <span className="text-[12px] text-white/70 truncate">
              BrandsMeetCreators <span className="text-white/45">• now</span>
            </span>
            <div className="flex-1" />
            <Bell className="h-3.5 w-3.5 text-white/45 shrink-0" />
            <ChevronUp className="h-4 w-4 text-white/45 shrink-0" />
          </div>
          <div
            className={`text-[15px] font-semibold leading-snug mt-1.5 break-words line-clamp-2 ${
              title.trim() ? 'text-white' : 'text-white/35'
            }`}
          >
            {title.trim() || 'Notification title'}
          </div>
          <div
            className={`text-[13.5px] leading-snug mt-0.5 break-words line-clamp-3 ${
              body.trim() ? 'text-white/80' : 'text-white/35'
            }`}
          >
            {body.trim() || 'Notification message will appear here…'}
          </div>
          {deepLink && (
            <div className="text-[10px] text-white/40 font-mono mt-1.5 truncate">
              {deepLink}
            </div>
          )}
        </div>
      </div>

      {/* Rest of the lock screen — stays dark/empty like a real phone,
          instead of the frame stopping right under the banner. */}
      <div className="flex-1" />
    </div>
  );
}
